import React from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Activity, 
  MapPin, 
  Server, 
  Users,
  Phone,
  FileCheck,
  Calendar
} from 'lucide-react';
import { Badge } from "../../ui/badge";

const practices = [
  {
    id: 1,
    title: "Live Example 1",
    location: "Maryland",
    server: "EagleSoft",
    staff: "1 Dentist",
    roi: "$28,454",
    production: "$26,350",
    metrics: [
      { label: "Tasks", value: "976", icon: FileCheck },
      { label: "Hygiene", value: "4", icon: Calendar },
      { label: "Verifs", value: "508", icon: FileCheck },
      { label: "New Pts", value: "24", icon: Users },
    ]
  },
  {
    id: 2,
    title: "Live Example 2",
    location: "California",
    server: "Curve Dental",
    staff: "2 Dentists, 1 Hygienist",
    roi: "$22,754",
    production: "$21,750",
    metrics: [
      { label: "Tasks", value: "1,079", icon: FileCheck },
      { label: "Hygiene", value: "7", icon: Calendar },
      { label: "Comms", value: "144", icon: FileCheck },
      { label: "New Pts", value: "15", icon: Users },
    ]
  },
  {
    id: 3,
    title: "Live Example 3",
    location: "Texas",
    server: "Dentrix",
    staff: "2 Dentists, 2 Hygienists",
    roi: "$8,629",
    production: "$7,850",
    metrics: [
      { label: "Tasks", value: "339", icon: FileCheck },
      { label: "Calls", value: "154", icon: Phone },
      { label: "Verifs", value: "121", icon: FileCheck },
      { label: "New Pts", value: "4", icon: Users },
    ]
  }
];

const RealROISlide = () => {
  return (
    <div className="h-full w-full bg-slate-950 flex items-center justify-center p-6 md:p-8 relative overflow-hidden text-white">
      {/* Background Ambience */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-emerald-600/10 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-[1600px] w-full h-full flex flex-col z-10">
        
        {/* Header Section */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 text-emerald-400 mb-3 font-mono text-sm tracking-wider uppercase bg-emerald-500/10 px-4 py-1.5 rounded-full border border-emerald-500/20">
            <Activity className="w-4 h-4" />
            <span>Live Performance Reports</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-b from-white to-slate-400">
            Real Practice ROI
          </h2>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-1 items-center">
            {practices.map((practice, index) => (
                <motion.div
                    key={practice.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="h-full"
                >
                    <div className="h-full bg-slate-900/40 backdrop-blur-sm border border-white/10 rounded-3xl overflow-hidden hover:border-white/20 transition-all duration-300 flex flex-col group relative">
                        {/* Hover Gradient */}
                        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                        {/* Card Header */}
                        <div className="p-6 border-b border-white/5 bg-white/5">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-xl font-bold text-white">{practice.title}</h3>
                                <div className="flex gap-2">
                                    <span className="text-[10px] uppercase tracking-wider bg-white/10 text-slate-300 px-2 py-1 rounded-md border border-white/10">
                                        {practice.location}
                                    </span>
                                </div>
                            </div>
                            <div className="flex gap-2 text-xs text-slate-400">
                                <div className="flex items-center gap-1">
                                    <Server className="w-3 h-3" /> {practice.server}
                                </div>
                                <span className="text-white/20">•</span>
                                <div className="flex items-center gap-1">
                                    <Users className="w-3 h-3" /> {practice.staff}
                                </div>
                            </div>
                        </div>

                        {/* Main Stats */}
                        <div className="p-8 text-center bg-gradient-to-b from-emerald-500/5 to-transparent relative overflow-hidden">
                             {/* Ambient Glow */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-emerald-500/20 blur-3xl rounded-full opacity-50" />
                            
                            <div className="relative z-10">
                                <div className="text-sm text-emerald-400 font-bold tracking-widest uppercase mb-2">Total Monthly ROI</div>
                                <div className="text-5xl font-bold text-white tracking-tight mb-4">{practice.roi}</div>
                            </div>
                        </div>

                        {/* Secondary Stats Grid */}
                        <div className="grid grid-cols-2 gap-px bg-white/10 flex-1">
                            {practice.metrics.map((metric, i) => (
                                <div key={i} className="bg-slate-900/60 p-4 flex flex-col items-center justify-center text-center group/item hover:bg-slate-800/60 transition-colors">
                                    <div className="text-2xl font-bold text-white mb-1">{metric.value}</div>
                                    <div className="flex items-center gap-1.5 text-xs text-slate-400 font-medium uppercase tracking-wide">
                                        <metric.icon className="w-3 h-3 text-blue-400" />
                                        {metric.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        {/* Production Footer */}
                        <div className="p-6 bg-white/5 border-t border-white/10">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-400">Production Increase</span>
                                <span className="text-xl font-bold text-white">{practice.production}</span>
                            </div>
                        </div>

                    </div>
                </motion.div>
            ))}
        </div>

      </div>
    </div>
  );
};

export default RealROISlide;
